import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MessageCircle, ChevronLeft, Car, Calendar, Headphones, 
  User, Clock, ChevronRight, Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { getProfilePhotoUrl, getVehiclePhotoUrl } from '../lib/imageUtils';

const MessagesPage = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const isSupport = user?.roles?.some(r => ['admin', 'super_admin', 'platform_owner'].includes(r));

  useEffect(() => {
    fetchThreads();
  }, [activeTab]);

  const fetchThreads = async () => {
    setLoading(true);
    try {
      let url = '/messages/threads';
      if (activeTab !== 'all') {
        url += `?thread_type=${activeTab}`;
      }
      const response = await api.get(url);
      setThreads(response.data.threads || []);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const getOtherParticipant = (thread) => {
    const others = thread.participants?.filter(p => p.user_id !== user?.user_id) || [];
    return others[0] || { full_name: 'Unknown' };
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return format(parseISO(timestamp), 'd MMM yyyy, h:mm a');
    } catch {
      return '';
    }
  };

  const getThreadTypeIcon = (type) => {
    switch (type) {
      case 'inquiry': return <Car className="w-4 h-4" />;
      case 'booking': return <Calendar className="w-4 h-4" />;
      case 'support': return <Headphones className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getThreadTypeLabel = (type) => {
    switch (type) {
      case 'inquiry': return 'Vehicle Inquiry';
      case 'booking': return 'Booking';
      case 'support': return 'Support';
      default: return type;
    }
  };

  const getDashboardRoute = () => {
    if (!user) return '/';
    if (user.roles?.includes('platform_owner')) return '/platform-owner';
    if (user.roles?.includes('super_admin')) return '/super-admin';
    if (user.roles?.includes('admin')) return '/admin';
    if (user.roles?.includes('owner')) return '/host';
    return '/dashboard';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex items-center gap-3 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(getDashboardRoute())} className="h-9 w-9">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#004B8D]" />
            <h1 className="font-heading text-lg sm:text-xl font-semibold">Messages</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-4 sm:py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
          <TabsList className="grid w-full grid-cols-4 h-auto">
            <TabsTrigger value="all" className="text-xs sm:text-sm py-2">All</TabsTrigger>
            <TabsTrigger value="inquiry" className="text-xs sm:text-sm py-2">Inquiries</TabsTrigger>
            <TabsTrigger value="booking" className="text-xs sm:text-sm py-2">Bookings</TabsTrigger>
            <TabsTrigger value="support" className="text-xs sm:text-sm py-2">Support</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Thread List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
          </div>
        ) : threads.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Inbox className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="font-medium text-slate-900 mb-1">No messages yet</h3>
              <p className="text-sm text-slate-500">
                {activeTab === 'all' 
                  ? 'Your conversations will appear here'
                  : `No ${getThreadTypeLabel(activeTab).toLowerCase()} messages`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {threads.map((thread) => {
              const other = getOtherParticipant(thread);
              const photoUrl = getProfilePhotoUrl(other.profile_photo);
              
              return (
                <Link
                  key={thread.thread_id}
                  to={`/messages/${thread.thread_id}`}
                  className="block"
                  data-testid={`thread-${thread.thread_id}`}
                >
                  <Card className={`border-slate-100 hover:border-[#004B8D]/30 transition-colors ${thread.unread_count > 0 ? 'bg-blue-50/50' : ''}`}>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          {photoUrl ? (
                            <img 
                              src={photoUrl}
                              alt={other.full_name}
                              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling?.classList?.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#004B8D] flex items-center justify-center text-white text-sm font-semibold ${photoUrl ? 'hidden' : ''}`}>
                            {other.full_name?.charAt(0)?.toUpperCase() || <User className="w-5 h-5" />}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className={`font-medium text-sm sm:text-base truncate ${thread.unread_count > 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                                  {other.full_name}
                                </h3>
                                <Badge variant="secondary" className="text-[10px] sm:text-xs flex items-center gap-1">
                                  {getThreadTypeIcon(thread.thread_type)}
                                  <span className="hidden sm:inline">{getThreadTypeLabel(thread.thread_type)}</span>
                                </Badge>
                              </div>
                              
                              {/* Vehicle/Booking reference */}
                              {thread.vehicle && (
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {thread.vehicle.make} {thread.vehicle.model}
                                </p>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {thread.unread_count > 0 && (
                                <Badge className="bg-[#004B8D] text-white text-xs">
                                  {thread.unread_count}
                                </Badge>
                              )}
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            </div>
                          </div>

                          {/* Last message preview */}
                          {thread.last_message && (
                            <p className={`text-sm mt-1 truncate ${thread.unread_count > 0 ? 'text-slate-700' : 'text-slate-500'}`}>
                              {thread.last_message.sender?.full_name === user?.full_name ? 'You: ' : ''}
                              {thread.last_message.body}
                            </p>
                          )}

                          {/* Timestamp */}
                          {thread.last_message_at && (
                            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTimestamp(thread.last_message_at)}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
